import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface CommentViewerBaseUnfurlProps {
  onUnfurlClick: Handler;

  interactionId: string;
  isInteractionDisabled?: boolean;
}

/*
 * Style.
 */

const unfurlWrapperStyles = css`
  margin: 4px 12px 12px 12px;
  width: 260px;
  border-radius: 6px;
  overflow: hidden;
`;

export const StyledUnfurlDiv = styled.div`
  ${unfurlWrapperStyles};
`;

const StyledUnfurlButton = styled(ChromelessButton)`
  ${unfurlWrapperStyles};
`;

const StyledUnfurlContainerDiv = styled.div`
  position: relative;
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  padding: 7px 8px 8px 8px;

  border-radius: 6px;
  background-color: ${(p) => p.theme.alphas.white60};

  transition: opacity 300ms ease;
  opacity: 0;

  ${StyledUnfurlContainerDiv}:hover & {
    opacity: 1;
  }
`;

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-template-areas: 'text icon';
  grid-template-columns: 1fr auto;
  grid-column-gap: 4px;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  grid-area: text;
  padding-left: 4px;
  font-weight: ${fontWeights.semibold};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  padding: 2px;
  margin-right: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Component.
 */

export const CommentViewerBaseUnfurl: FC<PropsWithChildren<CommentViewerBaseUnfurlProps>> = (props) => {
  const {onUnfurlClick, interactionId, isInteractionDisabled, children} = props;

  // Don't render a button if its disabled, or you'll confuse accessibility peeps.
  if (isInteractionDisabled) {
    return <StyledUnfurlDiv>{children}</StyledUnfurlDiv>;
  }

  return (
    <StyledUnfurlButton onClick={onUnfurlClick} interactionId={interactionId}>
      <StyledUnfurlContainerDiv>
        {children}
        {renderUnfurlOverlay()}
      </StyledUnfurlContainerDiv>
    </StyledUnfurlButton>
  );
};

/*
 * Helpers.
 */

function renderUnfurlOverlay() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledOverlayDiv>
          <StyledDetailsDiv>
            <StyledTextDiv>
              <Trans id="comment-unfurl-link-view-in-new-window">View in new window</Trans>
            </StyledTextDiv>
            <StyledIconDiv>
              <Icon name="externalLinkTiny" color={theme.greys.white} />
            </StyledIconDiv>
          </StyledDetailsDiv>
        </StyledOverlayDiv>
      )}
    </UserThemeConsumer>
  );
}
